import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from "recharts";
import { Users, Sprout, GraduationCap, UserCheck } from "lucide-react";

import { useDashboard } from "@/lib/dataContext";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { ChartCard } from "@/components/common/ChartCard";
import { ChartTooltip, axisProps, gridProps } from "@/components/common/ChartTooltip";
import { fmt, fmtPct, chartColor } from "@/lib/format";

const DonutLegend = ({ items, total }) => (
    <div className="space-y-4">
        {items.map((d) => (
            <div key={d.name} className="flex items-center gap-3">
                <span className="h-3 w-3 rounded-full" style={{ background: d.color }} />
                <div>
                    <p className="text-xl font-semibold tabular text-foreground">{fmt(d.value)}</p>
                    <p className="text-xs text-muted-foreground">{d.name} &middot; {fmtPct((d.value / total) * 100)}</p>
                </div>
            </div>
        ))}
    </div>
);

export default function Membership() {
    const { data, stateMeta } = useDashboard();
    const m = data.membership_profile;
    const districts = data.district_drilldown;
    const stateName =
        data?.state_profile?.state_name ||
        data?.executive_summary?.state_name ||
        data?.entity?.state_name ||
        stateMeta?.state_name ||
        "State";

    const gents = m.gender.gents_total;
    const ladies = m.gender.ladies_total;
    const totalYouthMembers = m.youth_gender.total_youth;

    const genderData = [
        { name: "Gents", value: gents, color: chartColor(2) },
        { name: "Ladies", value: ladies, color: chartColor(4) },
    ];
    const youthData = [
        { name: "Youth", value: totalYouthMembers, color: chartColor(1) },
        { name: "Seniors", value: m.approved_members - totalYouthMembers, color: chartColor(6) },
    ];
    const youthGenderData = [
        { name: "Youth Gents", value: m.youth_gender.gents_youth, color: chartColor(2) },
        { name: "Youth Ladies", value: m.youth_gender.ladies_youth, color: chartColor(4) },
    ];

    const topYouthDistricts = [...districts]
        .filter((d) => d.membership.approved_members > 0)
        .sort((a, b) => b.membership.youth_share_pct - a.membership.youth_share_pct)
        .slice(0, 12)
        .map((d) => ({
            name: d.district_name.length > 12 ? d.district_name.slice(0, 11) + "…" : d.district_name,
            "Youth %": d.membership.youth_share_pct,
        }));

    return (
        <div className="space-y-8">
            <PageHeader
                eyebrow="Membership"
                title="Membership Overview"
                description={`A current snapshot of membership across the ${String(stateName).trim()} organisation.`}
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <KpiCard index={0} label="Approved Members" value={fmt(m.approved_members)} sub="Total strength" icon={Users} tone="primary" />
                <KpiCard index={1} label="Youth Members" value={fmt(totalYouthMembers)} sub={`${fmtPct(m.youth_share_pct)} of members`} icon={Sprout} tone="success" />
                <KpiCard index={2} label="Bal Vikas Gurus" value={fmt(m.bal_vikas.gurus_total)} sub={`${fmt(m.bal_vikas.gurus_gents)} gents · ${fmt(m.bal_vikas.gurus_ladies)} ladies`} icon={GraduationCap} tone="accent" />
                <KpiCard index={3} label="Bal Vikas Alumni" value={fmt(m.bal_vikas.approved_alumni)} sub="Grown-up students" icon={UserCheck} tone="warning" />
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <ChartCard title="Gender Composition" description="All approved members">
                    <div className="flex flex-col items-center gap-6 sm:flex-row sm:justify-around">
                        <div className="h-[190px] w-[190px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={genderData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={52} outerRadius={80} paddingAngle={2} stroke="hsl(var(--card))" strokeWidth={2}>
                                        {genderData.map((d, i) => <Cell key={i} fill={d.color} />)}
                                    </Pie>
                                    <Tooltip content={<ChartTooltip formatter={(v) => fmt(v)} />} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <DonutLegend items={genderData} total={m.approved_members} />
                    </div>
                </ChartCard>

                <ChartCard title="Youth vs Seniors" description="Age-based membership split">
                    <div className="flex flex-col items-center gap-6 sm:flex-row sm:justify-around">
                        <div className="h-[190px] w-[190px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={youthData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={52} outerRadius={80} paddingAngle={2} stroke="hsl(var(--card))" strokeWidth={2}>
                                        {youthData.map((d, i) => <Cell key={i} fill={d.color} />)}
                                    </Pie>
                                    <Tooltip content={<ChartTooltip formatter={(v) => fmt(v)} />} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <DonutLegend items={youthData} total={m.approved_members} />
                    </div>
                </ChartCard>

                <ChartCard title="Youth by Gender" description="Gender split within youth members">
                    <div className="flex flex-col items-center gap-6 sm:flex-row sm:justify-around">
                        <div className="h-[190px] w-[190px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={youthGenderData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={52} outerRadius={80} paddingAngle={2} stroke="hsl(var(--card))" strokeWidth={2}>
                                        {youthGenderData.map((d, i) => <Cell key={i} fill={d.color} />)}
                                    </Pie>
                                    <Tooltip content={<ChartTooltip formatter={(v) => fmt(v)} />} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <DonutLegend items={youthGenderData} total={totalYouthMembers} />
                    </div>
                </ChartCard>
            </div>

            <ChartCard title="Youth Share by District" description="Top 12 districts by youth membership proportion">
                <div className="h-[360px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={topYouthDistricts} layout="vertical" margin={{ top: 0, right: 24, left: 8, bottom: 0 }}>
                            <CartesianGrid {...gridProps} horizontal={false} vertical />
                            <XAxis type="number" {...axisProps} unit="%" />
                            <YAxis type="category" dataKey="name" {...axisProps} width={92} />
                            <Tooltip content={<ChartTooltip formatter={(v) => fmtPct(v)} />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
                            <Bar dataKey="Youth %" fill={chartColor(1)} radius={[0, 6, 6, 0]} maxBarSize={22} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </ChartCard>
        </div>
    );
}
